// Approved Featured submissions with like_count + viewer's has_liked state.
// Drives the vertical reels feed in the Aura tab.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface FeedItem {
  id: string;
  user_id: string;
  kind: string;
  template_id: string;
  template_label: string | null;
  caption: string | null;
  permanent_image_path: string;
  display_order: number;
  created_at: string;
  like_count: number;
  has_liked: boolean;
  creator_name: string;
  instagram_handle: string | null;
}

export function useFeaturedFeed(enabled = true, limit = 50) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(enabled);

  const reload = useCallback(async () => {
    const { data } = await supabase.rpc("list_featured_feed", { _limit: limit });
    setItems((data ?? []) as FeedItem[]);
    setLoading(false);
  }, [limit]);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  /** Optimistic toggle that also calls the server. */
  const toggleLike = useCallback(async (id: string) => {
    let prevLiked = false;
    setItems((arr) =>
      arr.map((it) => {
        if (it.id !== id) return it;
        prevLiked = it.has_liked;
        return {
          ...it,
          has_liked: !it.has_liked,
          like_count: Math.max(0, it.like_count + (it.has_liked ? -1 : 1)),
        };
      }),
    );
    const { data, error } = await supabase.rpc("toggle_featured_like", {
      _submission_id: id,
    });
    if (error) {
      // revert
      setItems((arr) =>
        arr.map((it) =>
          it.id === id
            ? {
                ...it,
                has_liked: prevLiked,
                like_count: Math.max(0, it.like_count + (prevLiked ? 1 : -1)),
              }
            : it,
        ),
      );
      return { ok: false as const, error };
    }
    const row = (data ?? [])[0] as { liked: boolean; like_count: number } | undefined;
    if (row) {
      setItems((arr) =>
        arr.map((it) =>
          it.id === id ? { ...it, has_liked: row.liked, like_count: row.like_count } : it,
        ),
      );
    }
    return { ok: true as const };
  }, []);

  return { items, loading, reload, toggleLike };
}
